import { useEffect, useRef, useState, useCallback } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import Overlay from 'ol/Overlay';
import { easeOut } from 'ol/easing';
import 'ol/ol.css';

// Continent center coordinates for quick navigation
const CONTINENT_CENTERS = {
  'Africa': { center: [20, 0], zoom: 3 },
  'Asia': { center: [100, 35], zoom: 3 },
  'Europe': { center: [15, 50], zoom: 4 },
  'North America': { center: [-100, 45], zoom: 3 },
  'South America': { center: [-60, -15], zoom: 3 },
  'Oceania': { center: [140, -25], zoom: 3 },
  'Atlantic': { center: [-35, 47], zoom: 4 },  // Added for SportsFuture Island
};

// Zoom threshold - planned locations only visible above this zoom level (when toggle is OFF)
// Set to 5 so they only appear when zoomed into a continent/region
const PLANNED_VISIBILITY_ZOOM = 5;

const OpenLayersMapEnhanced = ({ 
  locations = [], 
  onLocationSelect, 
  selectedLocation,
  hoveredLocationId,
  onLocationHover,
  onMapReady,
  showPlannedLocations = false,
  className = ''
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const plannedSourceRef = useRef(null);
  const plannedLayerRef = useRef(null);
  const activeOverlaysRef = useRef({}); // Use object for overlay storage
  const [isMapReady, setIsMapReady] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(2);

  // Store refs for hover/selection state that can be accessed in style callbacks
  const hoveredIdRef = useRef(hoveredLocationId);
  const selectedIdRef = useRef(selectedLocation?.id);
  const currentZoomRef = useRef(currentZoom);
  const showPlannedLocationsRef = useRef(false); // Default to false - planned dots hidden
  
  useEffect(() => {
    hoveredIdRef.current = hoveredLocationId;
  }, [hoveredLocationId]);
  
  useEffect(() => {
    selectedIdRef.current = selectedLocation?.id;
  }, [selectedLocation]);

  useEffect(() => {
    currentZoomRef.current = currentZoom;
  }, [currentZoom]);

  // Update the ref when prop changes - sync immediately
  useEffect(() => {
    showPlannedLocationsRef.current = showPlannedLocations;
    // Refresh the planned layer to reflect the change
    if (plannedLayerRef.current) {
      plannedLayerRef.current.changed();
    }
  }, [showPlannedLocations]);

  // Also sync on initial mount
  useEffect(() => {
    showPlannedLocationsRef.current = showPlannedLocations;
  }, []);

  // Inject CSS animations for pulsing markers
  useEffect(() => {
    const styleId = 'ol-marker-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes marker-pulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes island-glow {
          0%, 100% { box-shadow: 0 0 10px rgba(0, 123, 255, 0.5), 0 0 20px rgba(0, 123, 255, 0.3); }
          50% { box-shadow: 0 0 20px rgba(0, 123, 255, 0.8), 0 0 40px rgba(0, 123, 255, 0.5); }
        }
        @keyframes island-float {
          0%, 100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-3px); }
        }
        .active-marker-wrapper {
          position: absolute;
          top: 0; left: 0;
          transform: translate(-50%, -50%);
          pointer-events: auto;
          cursor: pointer;
        }
        .active-marker-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }
        .pulse-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: rgba(34, 197, 94, 0.5);
          animation: marker-pulse 2s ease-out infinite;
          pointer-events: none;
        }
        .pulse-ring.delayed {
          animation-delay: 0.5s;
        }
        .marker-core {
          position: relative;
          width: 18px; 
          height: 18px;
          border-radius: 50%;
          background: #22c55e;
          border: 3px solid #ffffff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: all 0.2s ease;
          z-index: 10;
        }
        .marker-core:hover {
          transform: scale(1.1);
        }
        .marker-core.highlighted {
          width: 22px; 
          height: 22px;
          border-color: #1e40af;
          box-shadow: 0 2px 12px rgba(30, 64, 175, 0.5);
        }
        .marker-dot {
          position: absolute;
          top: 50%; 
          left: 50%;
          transform: translate(-50%, -50%);
          width: 6px; 
          height: 6px;
          border-radius: 50%;
          background: white;
        }
        
        /* Custom Island Marker Styles */
        .island-marker-wrapper {
          position: absolute;
          top: 0; left: 0;
          transform: translate(-50%, -50%);
          pointer-events: auto;
          cursor: pointer;
          animation: island-float 3s ease-in-out infinite;
        }
        .island-marker-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .island-pulse-ring {
          position: absolute;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(0, 123, 255, 0.3);
          animation: marker-pulse 2.5s ease-out infinite;
          pointer-events: none;
        }
        .island-pulse-ring.delayed {
          animation-delay: 0.8s;
        }
        .island-icon-wrapper {
          position: relative;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          border: 3px solid #007BFF;
          box-shadow: 0 4px 15px rgba(0, 123, 255, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          z-index: 10;
          animation: island-glow 2s ease-in-out infinite;
          transition: all 0.3s ease;
        }
        .island-icon-wrapper:hover {
          transform: scale(1.15);
          border-color: #0056b3;
        }
        .island-icon-wrapper.highlighted {
          width: 56px;
          height: 56px;
          border-width: 4px;
          border-color: #1e40af;
        }
        .island-icon-wrapper img {
          width: 36px;
          height: 36px;
          object-fit: contain;
        }
        .island-icon-wrapper.highlighted img {
          width: 42px;
          height: 42px;
        }
        .island-label {
          position: absolute;
          bottom: -24px;
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          background: rgba(0, 123, 255, 0.9);
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Create HTML element for custom island marker
  const createIslandMarkerElement = useCallback((location, isHovered, isSelected) => {
    const isHighlighted = isHovered || isSelected;
    
    const wrapper = document.createElement('div');
    wrapper.className = 'island-marker-wrapper';
    wrapper.dataset.locationId = location.id;
    
    const container = document.createElement('div');
    container.className = 'island-marker-container';
    
    // Pulse rings
    const pulse1 = document.createElement('div');
    pulse1.className = 'island-pulse-ring';
    
    const pulse2 = document.createElement('div');
    pulse2.className = 'island-pulse-ring delayed';
    
    // Icon wrapper with logo
    const iconWrapper = document.createElement('div');
    iconWrapper.className = `island-icon-wrapper ${isHighlighted ? 'highlighted' : ''}`;
    
    const logo = document.createElement('img');
    logo.src = '/SFUT_icon_small.png';
    logo.alt = 'SportsFuture Island';
    iconWrapper.appendChild(logo);
    
    // Label
    const label = document.createElement('div');
    label.className = 'island-label';
    label.textContent = 'HQ';
    
    container.appendChild(pulse1);
    container.appendChild(pulse2);
    container.appendChild(iconWrapper);
    container.appendChild(label);
    wrapper.appendChild(container);
    
    return wrapper;
  }, []);

  // Create HTML element for active marker overlay
  const createActiveMarkerElement = useCallback((location, isHovered, isSelected) => {
    // Check if this is the custom island
    if (location.isCustomIsland) {
      return createIslandMarkerElement(location, isHovered, isSelected);
    }
    
    const isHighlighted = isHovered || isSelected;
    
    // Outer wrapper handles the transform
    const wrapper = document.createElement('div');
    wrapper.className = 'active-marker-wrapper';
    wrapper.dataset.locationId = location.id;
    
    // Container for marker and pulse rings
    const container = document.createElement('div');
    container.className = 'active-marker-container';
    
    // Pulse rings (centered in container)
    const pulse1 = document.createElement('div');
    pulse1.className = 'pulse-ring';
    
    const pulse2 = document.createElement('div');
    pulse2.className = 'pulse-ring delayed';
    
    // Main marker circle
    const marker = document.createElement('div');
    marker.className = `marker-core ${isHighlighted ? 'highlighted' : ''}`;
    
    // Inner white dot
    const dot = document.createElement('div');
    dot.className = 'marker-dot';
    marker.appendChild(dot);
    
    container.appendChild(pulse1);
    container.appendChild(pulse2);
    container.appendChild(marker);
    wrapper.appendChild(container);
    
    return wrapper;
  }, [createIslandMarkerElement]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Vector source for planned locations
    const plannedSource = new VectorSource();
    plannedSourceRef.current = plannedSource;

    // Planned locations layer with dynamic styling
    const plannedLayer = new VectorLayer({
      source: plannedSource,
      style: (feature) => {
        const location = feature.get('location');
        const isHovered = location.id === hoveredIdRef.current;
        const isSelected = location.id === selectedIdRef.current;
        const isHighlighted = isHovered || isSelected;
        const zoom = currentZoomRef.current;
        const showPlanned = showPlannedLocationsRef.current; // Use ref instead of prop
        
        // Hide at world view unless highlighted or showPlannedLocations is ON
        // When toggle is ON (showPlanned = true), always show regardless of zoom
        if (!showPlanned && zoom < PLANNED_VISIBILITY_ZOOM && !isHighlighted) {
          return new Style({});
        }
        
        // Adjust size based on zoom level for better visibility at world view
        let baseRadius = 6;
        if (zoom < 3) {
          baseRadius = 4; // Smaller at very zoomed out
        } else if (zoom < 4) {
          baseRadius = 5;
        }
        
        const radius = isHighlighted ? baseRadius + 4 : baseRadius;
        const fillOpacity = isHighlighted ? 0.9 : (zoom >= PLANNED_VISIBILITY_ZOOM ? 0.7 : 0.5);
        
        return new Style({
          image: new CircleStyle({
            radius: radius,
            fill: new Fill({ 
              color: isHighlighted 
                ? 'rgba(59, 130, 246, 0.9)' 
                : `rgba(156, 163, 175, ${fillOpacity})` 
            }),
            stroke: new Stroke({
              color: isHighlighted ? '#1e40af' : '#ffffff',
              width: isHighlighted ? 3 : 2
            })
          }),
          zIndex: isHighlighted ? 50 : 1
        });
      },
      zIndex: 10,
      updateWhileAnimating: true,
      updateWhileInteracting: true
    });
    plannedLayerRef.current = plannedLayer;

    // Esri World Street Map - English labels with blue oceans
    const tileLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
        attributions: 'Tiles © Esri',
        maxZoom: 19
      })
    });

    // Create map
    const map = new Map({
      target: mapRef.current,
      layers: [tileLayer, plannedLayer],
      view: new View({
        center: fromLonLat([0, 20]),
        zoom: 2,
        maxZoom: 18,
        minZoom: 1.5
      }),
      controls: []
    });

    mapInstanceRef.current = map;

    // Track zoom changes
    map.getView().on('change:resolution', () => {
      const zoom = map.getView().getZoom();
      setCurrentZoom(zoom);
      currentZoomRef.current = zoom;
      // Refresh planned layer for visibility changes
      plannedLayer.changed();
    });

    // Handle clicks on planned features
    map.on('click', (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (f) => f);
      if (feature) {
        const location = feature.get('location');
        if (location) {
          onLocationSelect?.(location);
        }
      }
    });

    // Handle hover on planned features
    map.on('pointermove', (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (f) => f);
      if (feature) {
        const location = feature.get('location');
        if (location && onLocationHover) {
          onLocationHover(location.id);
        }
        map.getTarget().style.cursor = 'pointer';
      } else {
        // Only clear if we're not hovering over an active marker overlay
        const element = document.elementFromPoint(event.originalEvent.clientX, event.originalEvent.clientY);
        if (!element?.closest('.active-marker-container') && !element?.closest('.island-marker-container')) {
          if (onLocationHover) {
            onLocationHover(null);
          }
          map.getTarget().style.cursor = '';
        }
      }
    });

    setIsMapReady(true);

    return () => {
      // Cleanup overlays
      Object.values(activeOverlaysRef.current).forEach(({ overlay }) => {
        map.removeOverlay(overlay);
      });
      activeOverlaysRef.current = {};
      map.setTarget(undefined);
      mapInstanceRef.current = null;
    };
  }, []);

  // Expose map controls when ready
  useEffect(() => {
    if (isMapReady && onMapReady) {
      onMapReady({ flyTo, resetView, flyToContinent });
    }
  }, [isMapReady]);

  // Refresh planned layer when hover/selection changes
  useEffect(() => {
    if (plannedLayerRef.current) {
      plannedLayerRef.current.changed();
    }
  }, [hoveredLocationId, selectedLocation, currentZoom, showPlannedLocations]);

  // Update locations on map
  useEffect(() => {
    if (!plannedSourceRef.current || !mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    
    // Clear existing
    plannedSourceRef.current.clear();
    Object.values(activeOverlaysRef.current).forEach(({ overlay }) => {
      map.removeOverlay(overlay);
    });
    activeOverlaysRef.current = {};

    // Separate by status
    const activeLocations = locations.filter(loc => loc.status === 'active');
    const plannedLocations = locations.filter(loc => loc.status !== 'active');

    // Add planned as vector features
    const plannedFeatures = plannedLocations.map(location => {
      const feature = new Feature({
        geometry: new Point(fromLonLat(location.coordinates)),
        location: location
      });
      return feature;
    });
    plannedSourceRef.current.addFeatures(plannedFeatures);

    // Add active as HTML overlays for animation
    activeLocations.forEach(location => {
      const isHovered = location.id === hoveredLocationId;
      const isSelected = location.id === selectedLocation?.id;
      
      const element = createActiveMarkerElement(location, isHovered, isSelected);
      
      // Event handlers
      element.addEventListener('click', (e) => {
        e.stopPropagation();
        onLocationSelect?.(location);
      });
      
      element.addEventListener('mouseenter', () => {
        onLocationHover?.(location.id);
      });
      
      element.addEventListener('mouseleave', () => {
        onLocationHover?.(null);
      });
      
      const overlay = new Overlay({
        element: element,
        position: fromLonLat(location.coordinates),
        positioning: 'center-center',
        stopEvent: false
      });
      
      map.addOverlay(overlay);
      activeOverlaysRef.current[location.id] = { overlay, location };
    });

    console.log(`Map: ${activeLocations.length} active (animated), ${plannedLocations.length} planned`);
  }, [locations, createActiveMarkerElement]);

  // Update active marker styles when hover/selection changes
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    Object.values(activeOverlaysRef.current).forEach(({ overlay, location }) => {
      const isHovered = location.id === hoveredLocationId;
      const isSelected = location.id === selectedLocation?.id;
      
      const newElement = createActiveMarkerElement(location, isHovered, isSelected);
      
      // Re-attach event handlers
      newElement.addEventListener('click', (e) => {
        e.stopPropagation();
        onLocationSelect?.(location);
      });
      newElement.addEventListener('mouseenter', () => {
        onLocationHover?.(location.id);
      });
      newElement.addEventListener('mouseleave', () => {
        onLocationHover?.(null);
      });
      
      overlay.setElement(newElement);
    });
  }, [hoveredLocationId, selectedLocation, createActiveMarkerElement]);

  // Navigation functions
  const flyTo = useCallback((location, zoom = 8) => {
    if (!mapInstanceRef.current || !location?.coordinates) return;
    mapInstanceRef.current.getView().animate({
      center: fromLonLat(location.coordinates),
      zoom: zoom,
      duration: 800,
      easing: easeOut
    });
  }, []);

  const resetView = useCallback(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.getView().animate({
      center: fromLonLat([0, 20]),
      zoom: 2,
      duration: 600,
      easing: easeOut
    });
  }, []);

  const flyToContinent = useCallback((continent) => {
    if (!mapInstanceRef.current) return;
    const config = CONTINENT_CENTERS[continent];
    if (!config) return;
    mapInstanceRef.current.getView().animate({
      center: fromLonLat(config.center),
      zoom: config.zoom,
      duration: 700,
      easing: easeOut
    });
  }, []);

  // Fly to selected location
  useEffect(() => {
    if (selectedLocation && mapInstanceRef.current) {
      flyTo(selectedLocation, 6);
    }
  }, [selectedLocation, flyTo]);

  const handleZoom = useCallback((delta) => {
    if (!mapInstanceRef.current) return;
    const view = mapInstanceRef.current.getView();
    view.animate({
      zoom: view.getZoom() + delta,
      duration: 250,
      easing: easeOut
    });
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={mapRef} 
        className="w-full h-full rounded-xl overflow-hidden"
        style={{ minHeight: '500px' }}
      />
      
      {/* Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
        <button
          onClick={resetView}
          className="w-10 h-10 bg-white hover:bg-gray-50 rounded-lg shadow-md border border-gray-200 
                     flex items-center justify-center transition-all duration-200 hover:shadow-lg
                     text-gray-600 hover:text-gray-900"
          title="Reset View"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
        <button
          onClick={() => handleZoom(1)}
          className="w-10 h-10 bg-white hover:bg-gray-50 rounded-lg shadow-md border border-gray-200 
                     flex items-center justify-center transition-all duration-200 hover:shadow-lg
                     text-gray-600 hover:text-gray-900 text-xl font-medium"
        >
          +
        </button>
        <button
          onClick={() => handleZoom(-1)}
          className="w-10 h-10 bg-white hover:bg-gray-50 rounded-lg shadow-md border border-gray-200 
                     flex items-center justify-center transition-all duration-200 hover:shadow-lg
                     text-gray-600 hover:text-gray-900 text-xl font-medium"
        >
          −
        </button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg shadow-md border border-gray-200 z-20">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-5 text-xs">
            <div className="flex items-center gap-2">
              <span className="relative flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500 border-2 border-white shadow"></span>
              </span>
              <span className="text-gray-700 font-medium">Active Office</span>
            </div>
            <div className="flex items-center gap-2">
              <img src="/SFUT_icon_small.png" alt="HQ" className="w-4 h-4" />
              <span className="text-blue-600 font-medium">Headquarters</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex rounded-full h-3 w-3 bg-gray-400"></span>
              <span className="text-gray-500 font-medium">Planned</span>
            </div>
          </div>
          {!showPlannedLocations && currentZoom < PLANNED_VISIBILITY_ZOOM && (
            <div className="text-xs text-blue-600 border-t border-gray-100 pt-2 flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
              Zoom in or enable "Planned" to see planned locations
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { CONTINENT_CENTERS };
export default OpenLayersMapEnhanced;
